import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RoastDetailModal from "@/components/RoastDetailModal";
import { Plus, X, Trash2, Coffee, ChevronRight, Save, Camera, FileImage, Sparkles, Printer, Pencil, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

// Downscale + recompress an image File client-side before upload.
// Phones produce 5–10 MB photos that are slow to upload AND get rejected by
// Anthropic's 5 MB base64 limit. We cap the longest side to 1800 px and use
// JPEG q=0.82 — typically yields 300–800 KB which uploads in a second or two.
async function compressImage(file) {
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size <= 800_000) return file; // already small, skip
  try {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1800;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file; // best-effort; fall back to original
  }
}

const SCORE_KEYS = [
  ["fragrance",  "Fragrance / Aroma"],
  ["flavour",    "Flavour"],
  ["aftertaste", "Aftertaste"],
  ["acidity",    "Acidity"],
  ["body",       "Body"],
  ["balance",    "Balance"],
  ["sweetness",  "Sweetness"],
  ["clean_cup",  "Clean cup"],
  ["uniformity", "Uniformity"],
  ["overall",    "Overall"],
];

// Agtron number → roast label (per SCAA/ProRoast convention)
function agtronBucket(n) {
  if (n == null || n === "" || isNaN(parseFloat(n))) return null;
  const v = parseFloat(n);
  if (v >= 80 && v <= 100) return { label: "Light Roast",  tone: "bg-amber-100 text-amber-900 border-amber-300" };
  if (v >= 66 && v <= 79)  return { label: "Medium-Light", tone: "bg-orange-100 text-orange-900 border-orange-300" };
  if (v >= 50 && v <= 65)  return { label: "Medium Roast", tone: "bg-orange-200 text-orange-950 border-orange-400" };
  if (v >= 41 && v <= 49)  return { label: "Medium-Dark",  tone: "bg-stone-200 text-stone-900 border-stone-400" };
  if (v >= 25 && v <= 40)  return { label: "Dark Roast",   tone: "bg-stone-800 text-stone-100 border-stone-900" };
  return { label: "Out of range", tone: "bg-muted text-muted-foreground border-border" };
}

export default function Cupping() {
  const { id } = useParams();
  return id ? <CuppingDetail id={id} /> : <CuppingList />;
}

// --------------------------------------------------------- LIST
function CuppingList() {
  const { user } = useAuth();
  const canWrite = ["Owner", "ProductionManager", "Roaster"].includes(user?.role);
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", cupping_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/cuppings");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cuppings", form);
      nav(`/cupping/${data.id}`);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="cupping-list-page">
      <PageHeader eyebrow="Operations" title="Cupping"
        description="Quality sessions linking roast profiles to scores. Multi-cupper SCA-compatible scoring with TDS extraction yield, Agtron number per sample, AI particle analysis, and print/scan paper scorecards."
        actions={canWrite && (
          <button onClick={() => setOpen(true)} data-testid="cupping-add"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <Plus className="w-4 h-4" /> New session
          </button>
        )} />

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="cupping-table">
          <thead><tr className="bg-secondary">
            <th className="label-tiny px-4 py-3 text-left w-16">#</th>
            <th className="label-tiny px-4 py-3 text-left">Date</th>
            <th className="label-tiny px-4 py-3 text-left">Session</th>
            <th className="label-tiny px-4 py-3 text-right">Samples</th>
            <th className="label-tiny px-4 py-3 text-right">Cuppers</th>
            <th className="label-tiny px-4 py-3 text-right">Avg overall</th>
            <th className="label-tiny px-4 py-3 text-left">Created by</th>
            <th className="w-8"></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                <Coffee className="w-6 h-6 mx-auto mb-2 opacity-40" />No cupping sessions yet.
              </td></tr>
            )}
            {items.map((c) => {
              const cupperSet = new Set();
              (c.samples || []).forEach((sm) => (sm.scores || []).forEach((sc) => cupperSet.add(sc.user_id)));
              const avg = (c.samples || []).reduce((s, sm) => s + (sm.avg_overall || 0), 0) / Math.max(1, (c.samples || []).length);
              return (
                <tr key={c.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                  onClick={() => nav(`/cupping/${c.id}`)} data-testid={`cupping-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs font-bold">{c.session_number != null ? `#${c.session_number}` : "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.cupping_date}</td>
                  <td className="px-4 py-2.5 font-semibold">{c.name}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{(c.samples || []).length}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{cupperSet.size}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{cupperSet.size ? fmt(avg, 2) : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{c.created_by}</td>
                  <td className="px-2 py-2 text-right text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/40 p-0 sm:p-4" data-testid="cupping-modal">
          <div className="bg-card border border-border rounded-t sm:rounded shadow-xl max-w-md w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-display text-lg font-bold">New cupping session</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <Field label="Session name *">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="cupping-name" className="inp" />
              </Field>
              <Field label="Date *">
                <input required type="date" value={form.cupping_date} onChange={(e) => setForm({ ...form, cupping_date: e.target.value })}
                  data-testid="cupping-date" className="inp" />
              </Field>
              <Field label="Notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp" />
              </Field>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
                <button type="submit" data-testid="cupping-create" className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">Create &amp; open</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;padding:0.5rem 0.75rem;background:white;border:1px solid hsl(40 21% 87%);border-radius:0.25rem;font-size:0.875rem}.inp:focus{outline:none;border-color:hsl(20 39% 39%)}`}</style>
    </div>
  );
}

// -------------------------------------------------------- DETAIL
function CuppingDetail({ id }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [origins, setOrigins] = useState([]);
  const [roasts, setRoasts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editSample, setEditSample] = useState(null);
  const [scoreFor, setScoreFor] = useState(null);
  const [openRoast, setOpenRoast] = useState(null);

  const load = async () => {
    // Resilient: don't let an origins/roasts permission failure block the page
    const [c, os, rp] = await Promise.allSettled([
      api.get(`/cuppings/${id}`),
      api.get("/origins"),
      api.get("/roast-profiles", { params: { limit: 200 } }),
    ]);
    if (c.status === "fulfilled") setData(c.value.data);
    setOrigins(os.status === "fulfilled" ? os.value.data : []);
    setRoasts(rp.status === "fulfilled" ? (rp.value.data.items || []) : []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const removeSample = async (sample_id) => {
    if (!window.confirm("Remove this sample (and all its scores)?")) return;
    await api.delete(`/cuppings/${id}/samples/${sample_id}`); await load();
  };
  const remove = async () => {
    if (!window.confirm("Delete the entire cupping session?")) return;
    await api.delete(`/cuppings/${id}`); nav("/cupping");
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="cupping-detail-page">
      <button onClick={() => nav("/cupping")} className="text-sm text-muted-foreground hover:text-foreground mb-3">← All cuppings</button>
      <PageHeader eyebrow={`Cupping · ${data.cupping_date}`} title={data.name}
        description={data.notes || "Add samples below, then score each one. TDS auto-computes extraction yield. Enter Agtron number for auto roast-level labelling. Upload a particle-analyser screenshot for an AI grind report."}
        actions={(
          <div className="flex gap-2">
            <a href={`/cupping/${id}/report`} target="_blank" rel="noopener" data-testid="cupping-report-link"
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
              <FileText className="w-4 h-4" /> Print session report (PDF)
            </a>
            <a href={`/cupping/${id}/scorecard`} target="_blank" rel="noopener"
              className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded text-sm hover:bg-secondary">
              <Printer className="w-4 h-4" /> Print blank scorecard
            </a>
            {["Owner", "ProductionManager"].includes(user?.role) && (
              <button onClick={remove} className="inline-flex items-center gap-2 px-3 py-2 border border-destructive/30 text-destructive rounded text-sm hover:bg-destructive/10">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        )} />

      <div className="space-y-4">
        {(data.samples || []).map((s) => {
          const myScore = (s.scores || []).find((sc) => sc.user_id === user.id);
          return (
            <div key={s.sample_id} className="bg-card border border-border rounded">
              <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4 border-b border-border">
                <div className="min-w-0">
                  <p className="label-tiny mb-0.5">Sample · {s.label}</p>
                  <p className="font-display text-lg font-bold">{s.roast_origin_name || "(unlinked)"}</p>
                  {s.roast_date && (
                    <p className="text-xs text-muted-foreground font-mono">
                      {s.roast_number && s.roast_profile_id && (
                        <button
                          onClick={() => setOpenRoast(s.roast_profile_id)}
                          data-testid={`open-roast-${s.sample_id}`}
                          className="text-primary font-semibold hover:underline mr-1"
                          title="View roast curve">
                          Roast #{s.roast_number}
                        </button>
                      )}
                      {s.roast_number && " · "}
                      {new Date(s.roast_date).toLocaleDateString("en-ZA")}
                      {s.roast_loss_pct != null && ` · loss ${fmt(s.roast_loss_pct, 1)}%`}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <Mini label="TDS %" value={s.tds_pct != null ? fmt(s.tds_pct) : "—"} />
                  <Mini label="Yield (g)" value={s.yield_g != null ? fmt(s.yield_g, 1) : "—"} />
                  <Mini label="Dose (g)" value={s.dose_g != null ? fmt(s.dose_g, 1) : "—"} />
                  <Mini label="Extraction %" value={s.extraction_yield_pct != null ? fmt(s.extraction_yield_pct) : "—"} accent />
                  <Mini label="Moisture %" value={s.moisture != null ? fmt(s.moisture, 1) : "—"} />
                  <AgtronMini value={s.agtron} />
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="label-tiny mb-0.5">Avg overall</p>
                    <p className="font-display text-2xl font-bold tabular-nums">{s.avg_overall ? fmt(s.avg_overall) : "—"}</p>
                  </div>
                  <button onClick={() => setScoreFor(s)} data-testid={`score-${s.sample_id}`}
                    className="px-3 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
                    {myScore ? "Edit my score" : "Score sample"}
                  </button>
                  {["Owner", "ProductionManager"].includes(user?.role) && (
                    <button onClick={() => setEditSample(s)} data-testid={`edit-sample-${s.sample_id}`}
                      title="Edit sample values"
                      className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => removeSample(s.sample_id)} className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <AgtronSection cuppingId={id} sample={s} onChange={load} />
              {(s.scores || []).length > 0 && (
                <div className="px-4 sm:px-5 pb-4 pt-3 space-y-2" data-testid={`scores-${s.sample_id}`}>
                  <p className="label-tiny">Submitted scores · {s.scores.length} cupper{s.scores.length === 1 ? "" : "s"}</p>
                  {s.scores.map((sc, i) => {
                    const total = SCORE_KEYS.reduce((acc, [k]) => acc + (sc[k] || 0), 0) - (sc.defects || 0);
                    return (
                      <div key={i} className="bg-secondary/40 border border-border rounded p-3"
                           data-testid={`score-card-${s.sample_id}-${i}`}>
                        <div className="flex items-baseline justify-between mb-2">
                          <p className="font-semibold text-sm">{sc.user_name}</p>
                          <p className="font-display text-lg font-bold tabular-nums text-primary">
                            {total.toFixed(2)}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-3 gap-y-1.5 text-xs">
                          {SCORE_KEYS.map(([k, l]) => (
                            <div key={k} className="flex items-baseline justify-between gap-2 min-w-0">
                              <span className="text-muted-foreground truncate">{l}</span>
                              <span className="font-mono tabular-nums shrink-0">{sc[k] != null ? Number(sc[k]).toFixed(2) : "—"}</span>
                            </div>
                          ))}
                          {(sc.defects || 0) > 0 && (
                            <div className="flex items-baseline justify-between gap-2 min-w-0">
                              <span className="text-destructive">Defects</span>
                              <span className="font-mono tabular-nums text-destructive font-semibold shrink-0">−{sc.defects}</span>
                            </div>
                          )}
                        </div>
                        {sc.notes && (
                          <p className="mt-2 pt-2 border-t border-border/60 text-xs text-muted-foreground italic">
                            "{sc.notes}"
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={() => setAddOpen(true)} data-testid="add-sample"
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-dashed border-primary/40 text-primary rounded text-sm hover:bg-primary/5">
        <Plus className="w-4 h-4" /> Add sample
      </button>

      {addOpen && <AddSampleModal cuppingId={id} roasts={roasts} origins={origins}
        onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
      {editSample && <AddSampleModal cuppingId={id} roasts={roasts} origins={origins}
        editing={editSample}
        onClose={() => setEditSample(null)} onAdded={() => { setEditSample(null); load(); }} />}
      {scoreFor && <ScoreModal cuppingId={id} samples={data.samples || []}
        startIndex={(data.samples || []).findIndex((s) => s.sample_id === scoreFor.sample_id)}
        currentUserId={user.id}
        onClose={() => setScoreFor(null)} onSaved={() => { setScoreFor(null); load(); }} />}
      {openRoast && <RoastDetailModal roastId={openRoast} onClose={() => setOpenRoast(null)} />}
    </div>
  );
}

// ----------------------------------------------- PARTICLE ANALYSIS (image + AI, collapsible)
function AgtronSection({ cuppingId, sample, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    const compressed = await compressImage(file);
    const fd = new FormData(); fd.append("file", compressed);
    try {
      await api.post(`/cuppings/${cuppingId}/samples/${sample.sample_id}/agtron-image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setOpen(true);
      await onChange();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false); if (inputRef.current) inputRef.current.value = "";
    }
  };

  const has = !!sample.agtron_image_id;
  return (
    <div className="border-b border-border bg-secondary/20">
      <button onClick={() => setOpen(!open)} data-testid={`particle-toggle-${sample.sample_id}`}
        className="w-full px-5 py-3 flex items-center gap-2 text-left hover:bg-secondary/40">
        <FileImage className="w-4 h-4 text-primary" />
        <span className="label-tiny">Particle Analysis</span>
        {has && sample.agtron_ai_report && <span className="text-xs text-[hsl(132,18%,36%)]">· AI report ready</span>}
        {!has && <span className="text-xs text-muted-foreground">· not uploaded</span>}
        <span className="ml-auto text-xs text-muted-foreground">{open ? "Hide ▲" : "Show ▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {has ? (
                <img src={`${API}/cupping-images/${sample.agtron_image_id}`} alt="Particle analysis"
                  className="max-h-40 rounded border border-border" />
              ) : (
                <p className="text-xs text-muted-foreground">No image uploaded. Upload a screenshot of your particle-size analyser (Kruve / similar) to get an AI grind interpretation.</p>
              )}
              {sample.agtron_ai_report && (
                <div className="mt-3 bg-card border border-border rounded p-3 text-sm">
                  <p className="label-tiny mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[hsl(31,65%,53%)]" /> AI grind report</p>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap leading-relaxed">{sample.agtron_ai_report}</div>
                </div>
              )}
              {error && <p className="text-destructive text-xs mt-2">{error}</p>}
            </div>
            <div>
              <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(e) => upload(e.target.files?.[0])} className="hidden"
                data-testid={`particle-input-${sample.sample_id}`} />
              <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`particle-upload-${sample.sample_id}`}
                className="inline-flex items-center gap-2 px-3 py-2 border border-border bg-card rounded text-xs font-semibold hover:bg-secondary disabled:opacity-60">
                <Camera className="w-3.5 h-3.5" /> {busy ? "Analysing…" : (has ? "Replace" : "Upload + AI report")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------- Searchable roast-profile picker
// Used inside AddSampleModal. Hits `GET /roast-profiles?q=…` with a 200ms
// debounce so the user can type a partial roast number, a "#3452" prefix,
// or an origin name (e.g. "Brazil") and see matching candidates.
function RoastProfilePicker({ value, initialRoasts, origins, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(initialRoasts || []);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const boxRef = useRef(null);

  // Resolve the currently-selected roast for the closed-state label
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    const local = (initialRoasts || []).find((r) => r.id === value);
    if (local) { setSelected(local); return; }
    api.get(`/roast-profiles/${value}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [value, initialRoasts]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const params = { limit: 30 };
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/roast-profiles", { params });
        setResults(data.items || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const originName = (rp) => origins.find((o) => o.id === rp.origin_id)?.name || rp.origin_name || "—";
  const fmtLabel = (rp) => `#${rp.roast_number} · ${rp.start_datetime ? new Date(rp.start_datetime).toLocaleDateString("en-ZA") : "?"} · ${originName(rp)}`;

  return (
    <div ref={boxRef} className="relative" data-testid="sample-roast-picker">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="sample-roast"
        className="inp text-left flex items-center justify-between gap-2">
        <span className={selected ? "" : "text-muted-foreground"}>
          {selected ? fmtLabel(selected) : "— Search by roast # or origin —"}
        </span>
        {selected && (
          <span onClick={(e) => { e.stopPropagation(); onChange(""); setSelected(null); }}
            data-testid="sample-roast-clear"
            className="text-muted-foreground hover:text-foreground text-xs px-1">×</span>
        )}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-card border border-border rounded shadow-lg max-h-72 overflow-hidden flex flex-col">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Type roast number (e.g. 345) or origin name (e.g. Brazil)…"
            data-testid="sample-roast-search"
            className="px-3 py-2 text-sm border-b border-border outline-none bg-card" />
          <div className="overflow-y-auto flex-1">
            {busy && <p className="px-3 py-3 text-xs text-muted-foreground">Searching…</p>}
            {!busy && results.length === 0 && (
              <p className="px-3 py-3 text-xs text-muted-foreground">No roasts match.</p>
            )}
            {!busy && results.map((rp) => (
              <button key={rp.id} type="button"
                onClick={() => { onChange(rp.id); setSelected(rp); setOpen(false); setQ(""); }}
                data-testid={`sample-roast-option-${rp.id}`}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-secondary border-b border-border/40 last:border-b-0 ${value === rp.id ? "bg-primary/10 font-semibold" : ""}`}>
                <span className="font-mono">#{rp.roast_number}</span>
                <span className="text-muted-foreground"> · {rp.start_datetime ? new Date(rp.start_datetime).toLocaleDateString("en-ZA") : "?"}</span>
                <span> · {originName(rp)}</span>
                <span className="text-muted-foreground"> · {rp.green_weight_kg}kg</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------- ADD/EDIT SAMPLE MODAL
function AddSampleModal({ cuppingId, roasts, origins, onClose, onAdded, editing }) {
  const [form, setForm] = useState({
    label: editing?.label ?? "",
    roast_profile_id: editing?.roast_profile_id ?? "",
    tds_pct: editing?.tds_pct ?? "",
    yield_g: editing?.yield_g ?? "",
    dose_g: editing?.dose_g ?? "",
    moisture: editing?.moisture ?? "",
    agtron: editing?.agtron ?? "",
    notes: editing?.notes ?? "",
  });
  const [error, setError] = useState("");

  const tds = parseFloat(form.tds_pct), y = parseFloat(form.yield_g), d = parseFloat(form.dose_g);
  const extraction = (tds && y && d) ? (tds * y / d) : null;
  const agtronInfo = agtronBucket(form.agtron);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    const payload = {
      label: form.label,
      roast_profile_id: form.roast_profile_id || null,
      tds_pct: form.tds_pct === "" ? null : parseFloat(form.tds_pct),
      yield_g: form.yield_g === "" ? null : parseFloat(form.yield_g),
      dose_g: form.dose_g === "" ? null : parseFloat(form.dose_g),
      moisture: form.moisture === "" ? null : parseFloat(form.moisture),
      agtron: form.agtron === "" ? null : parseFloat(form.agtron),
      notes: form.notes,
    };
    try {
      if (editing) {
        await api.patch(`/cuppings/${cuppingId}/samples/${editing.sample_id}`, payload);
      } else {
        await api.post(`/cuppings/${cuppingId}/samples`, payload);
      }
      onAdded();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const originName = (rp) => origins.find((o) => o.id === rp.origin_id)?.name || rp.origin_name || "—";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 p-0 sm:p-4" data-testid="add-sample-modal">
      <div className="bg-card border border-border rounded-t sm:rounded shadow-xl max-w-xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-bold">{editing ? "Edit sample" : "Add sample"}</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Sample label *">
            <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. A · Brazil 2024-week-3" data-testid="sample-label" className="inp" />
          </Field>
          <Field label="Linked roast profile (optional)">
            <RoastProfilePicker
              value={form.roast_profile_id}
              initialRoasts={roasts}
              origins={origins}
              onChange={(rpId) => setForm({ ...form, roast_profile_id: rpId })}
            />
          </Field>

          <div>
            <p className="label-tiny mb-2">Extraction yield = TDS % × Yield ÷ Dose</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="TDS %">
                <input type="number" step="any" value={form.tds_pct} onChange={(e) => setForm({ ...form, tds_pct: e.target.value })} data-testid="sample-tds" className="inp" />
              </Field>
              <Field label="Yield (g)">
                <input type="number" step="any" value={form.yield_g} onChange={(e) => setForm({ ...form, yield_g: e.target.value })} data-testid="sample-yield" className="inp" />
              </Field>
              <Field label="Dose (g)">
                <input type="number" step="any" value={form.dose_g} onChange={(e) => setForm({ ...form, dose_g: e.target.value })} data-testid="sample-dose" className="inp" />
              </Field>
            </div>
            <div className="mt-2 bg-secondary rounded p-2 flex items-center justify-between text-sm">
              <span className="label-tiny">Computed extraction yield</span>
              <span className="font-display font-bold tabular-nums">{extraction != null ? `${fmt(extraction)}%` : "—"}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Moisture %">
              <input type="number" step="any" value={form.moisture} onChange={(e) => setForm({ ...form, moisture: e.target.value })} data-testid="sample-moisture" className="inp" />
            </Field>
            <Field label="Agtron number (25–100)">
              <input type="number" step="1" min="0" max="120" value={form.agtron}
                onChange={(e) => setForm({ ...form, agtron: e.target.value })}
                data-testid="sample-agtron" className="inp" placeholder="e.g. 55" />
              {agtronInfo && (
                <span className={`mt-1 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border font-bold ${agtronInfo.tone}`}
                  data-testid="sample-agtron-label">
                  {agtronInfo.label}
                </span>
              )}
              <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                Light 80–100 · Medium 50–65 · Dark 25–40
              </p>
            </Field>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            <button type="submit" data-testid="sample-submit" className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">{editing ? "Save changes" : "Add sample"}</button>
          </div>
        </form>
        <style>{`.inp{width:100%;padding:0.5rem 0.75rem;background:white;border:1px solid hsl(40 21% 87%);border-radius:0.25rem;font-size:0.875rem}.inp:focus{outline:none;border-color:hsl(20 39% 39%)}`}</style>
      </div>
    </div>
  );
}

// ------------------------------------------------------- SCORE MODAL (sliders)
// Multi-sample navigation: receives the full samples list and a startIndex
// so the cupper can flick through samples with Prev/Next buttons or a
// horizontal swipe gesture without closing the modal between each.
function ScoreModal({ cuppingId, samples, startIndex, currentUserId, onClose, onSaved }) {
  const [idx, setIdx] = useState(Math.max(0, startIndex || 0));
  const sample = samples[idx];
  const existing = (sample?.scores || []).find((sc) => sc.user_id === currentUserId);

  const [form, setForm] = useState(() => {
    const f = {};
    SCORE_KEYS.forEach(([k]) => f[k] = existing?.[k] ?? 6);
    f.defects = existing?.defects ?? 0;
    f.notes = existing?.notes ?? "";
    return f;
  });
  const [error, setError] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const cardRef = useRef(null);

  // Reload form when the user moves to a different sample
  useEffect(() => {
    const ex = (sample?.scores || []).find((sc) => sc.user_id === currentUserId);
    const f = {};
    SCORE_KEYS.forEach(([k]) => f[k] = ex?.[k] ?? 6);
    f.defects = ex?.defects ?? 0;
    f.notes = ex?.notes ?? "";
    setForm(f);
    setError("");
    // Scroll modal back to top so the cupper sees the sample header
    cardRef.current?.scrollTo({ top: 0, behavior: "instant" });
  // eslint-disable-next-line
  }, [idx, sample?.sample_id]);

  // Touch swipe — horizontal threshold 60px, ignore if user dragged vertically
  // (so it doesn't fight with native scroll inside the modal).
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.time;
    touchStart.current = null;
    if (Math.abs(dx) < 60) return;        // not enough horizontal travel
    if (Math.abs(dy) > Math.abs(dx)) return; // mostly vertical → ignore
    if (dt > 700) return;                  // too slow → probably not a swipe
    if (dx < 0 && idx < samples.length - 1) saveAndGo(idx + 1);
    if (dx > 0 && idx > 0) saveAndGo(idx - 1);
  };

  const scan = async (file) => {
    if (!file) return;
    setScanBusy(true); setError("");
    const compressed = await compressImage(file);
    const fd = new FormData(); fd.append("file", compressed);
    try {
      const { data } = await api.post(
        `/cuppings/${cuppingId}/samples/${sample.sample_id}/scorecard-photo`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      const sc = data.scores || {};
      const next = { ...form };
      SCORE_KEYS.forEach(([k]) => { if (sc[k] != null) next[k] = sc[k]; });
      if (sc.defects != null) next.defects = sc.defects;
      if (sc.notes) next.notes = sc.notes;
      setForm(next);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Scan failed");
    } finally {
      setScanBusy(false); if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Save the current form to the current sample. Returns true on success.
  const saveCurrent = async () => {
    setSaving(true);
    const payload = {};
    SCORE_KEYS.forEach(([k]) => payload[k] = parseFloat(form[k]) || 0);
    payload.defects = parseFloat(form.defects) || 0;
    payload.notes = form.notes || "";
    try {
      await api.post(`/cuppings/${cuppingId}/samples/${sample.sample_id}/scores`, payload);
      // Mirror the new score into the in-memory samples list so the
      // "existing" detection works if the user navigates back to this
      // sample within the same session.
      const mine = { user_id: currentUserId, ...payload };
      const list = sample.scores || [];
      const others = list.filter((sc) => sc.user_id !== currentUserId);
      sample.scores = [...others, mine];
      return true;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      return false;
    } finally { setSaving(false); }
  };

  const saveAndGo = async (newIdx) => {
    const ok = await saveCurrent();
    if (ok) setIdx(newIdx);
  };

  const submit = async (e) => {
    e.preventDefault();
    const ok = await saveCurrent();
    if (ok) onSaved();
  };

  const total = SCORE_KEYS.reduce((s, [k]) => s + (parseFloat(form[k]) || 0), 0) - (parseFloat(form.defects) || 0);
  const canPrev = idx > 0;
  const canNext = idx < samples.length - 1;

  if (!sample) { onClose(); return null; }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 p-0 sm:p-4" data-testid="score-modal">
      <div ref={cardRef}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        className="bg-card border border-border rounded-t sm:rounded shadow-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="min-w-0">
            <p className="label-tiny">Score sample · {idx + 1} of {samples.length}</p>
            <h3 className="font-display text-lg font-bold truncate">{sample.label}</h3>
            {sample.roast_origin_name && (
              <p className="text-xs text-muted-foreground truncate">{sample.roast_origin_name}{sample.roast_number ? ` · #${sample.roast_number}` : ""}</p>
            )}
          </div>
          <button onClick={onClose} data-testid="score-close"><X className="w-4 h-4" /></button>
        </div>

        {samples.length > 1 && (
          <div className="flex items-center justify-between px-5 py-2 bg-secondary/40 border-b border-border sticky top-[73px] z-10">
            <button type="button" onClick={() => canPrev && saveAndGo(idx - 1)}
              disabled={!canPrev || saving}
              data-testid="score-prev"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-border bg-card hover:bg-secondary disabled:opacity-40">
              ← Prev
            </button>
            <p className="text-[11px] text-muted-foreground">Swipe ← → to switch (autosaves)</p>
            <button type="button" onClick={() => canNext && saveAndGo(idx + 1)}
              disabled={!canNext || saving}
              data-testid="score-next"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-border bg-card hover:bg-secondary disabled:opacity-40">
              Next →
            </button>
          </div>
        )}

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Scan paper card */}
          <div className="bg-secondary/40 border border-dashed border-primary/30 rounded p-3 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Scan paper scorecard</p>
              <p className="text-xs text-muted-foreground">Upload a photo of a hand-filled card — AI will pre-fill the sliders below.</p>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
              onChange={(e) => scan(e.target.files?.[0])} className="hidden"
              data-testid="scorecard-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={scanBusy}
              data-testid="scorecard-scan"
              className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
              {scanBusy ? "Reading…" : "Scan"}
            </button>
          </div>

          <div className="space-y-3">
            {SCORE_KEYS.map(([k, label]) => (
              <SliderRow key={k} label={label} value={parseFloat(form[k]) || 0}
                onChange={(v) => setForm({ ...form, [k]: v })} testid={`score-${k}`} />
            ))}
            <SliderRow label="Defects" value={parseFloat(form.defects) || 0}
              onChange={(v) => setForm({ ...form, defects: v })} testid="score-defects" max={4} step={1} bad />
          </div>

          <Field label="Tasting notes">
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Citrus, stone fruit, milk chocolate finish…"
              className="w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary" />
          </Field>

          <div className="bg-secondary rounded p-3 flex items-center justify-between">
            <p className="label-tiny">Computed total</p>
            <p className="font-display text-2xl font-bold tabular-nums">{total.toFixed(2)}</p>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            {canNext ? (
              <button type="button" disabled={saving} onClick={() => saveAndGo(idx + 1)}
                data-testid="score-save-next"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <Save className="w-4 h-4" /> Save &amp; Next →
              </button>
            ) : (
              <button type="submit" data-testid="score-save" disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <Save className="w-4 h-4" /> Save my score
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function SliderRow({ label, value, onChange, testid, max = 10, step = 0.25, bad }) {
  return (
    <div className="grid grid-cols-[140px_1fr_60px] gap-3 items-center">
      <span className="text-sm">{label}</span>
      <input type="range" min="0" max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className={`w-full accent-primary ${bad ? "accent-destructive" : ""}`} />
      <span className={`font-mono text-sm tabular-nums text-right ${bad && value > 0 ? "text-destructive font-semibold" : ""}`}>
        {Number(value).toFixed(step < 1 ? 2 : 0)}
      </span>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label-tiny mb-1.5 block">{label}</label>{children}</div>;
}

function Mini({ label, value, accent }) {
  return (
    <div className={`px-3 py-1.5 rounded text-center min-w-[70px] ${accent ? "bg-primary/10 border border-primary/30" : "bg-secondary"}`}>
      <p className="label-tiny mb-0.5">{label}</p>
      <p className="font-mono text-sm font-semibold">{value}</p>
    </div>
  );
}

function AgtronMini({ value }) {
  const bucket = agtronBucket(value);
  return (
    <div className="px-3 py-1.5 rounded text-center min-w-[70px] bg-secondary" data-testid="agtron-mini">
      <p className="label-tiny mb-0.5">Agtron</p>
      <p className="font-mono text-sm font-semibold">{value != null && value !== "" ? value : "—"}</p>
      {bucket && (
        <span className={`mt-1 inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${bucket.tone}`}>
          {bucket.label}
        </span>
      )}
    </div>
  );
}
